import { httpClient } from 'services/live4tv-client/http-client';

const config = require('config/app.config');

/**
 * Simplified LiveStudio API Service - Only for token generation
 *
 * This service handles only the token generation through your existing private API.
 * All other operations are handled directly with the LiveKit client.
 */
class SimpleLiveStudioApiService {
    private apiUrl = '/api/v1/livestudio';

    /**
     * Generate a token for accessing a LiveKit room
     * @param token User authentication token
     * @param roomName Name of the room to join
     * @param participantIdentity Unique identifier for the participant
     * @param participantName Display name for the participant
     * @param permissions Permissions for the participant
     * @returns Promise<string> JWT token for accessing the room
     */
    generateToken = async (
        token: string,
        roomName: string,
        participantIdentity: string,
        participantName: string,
        permissions: {
            canPublish?: boolean;
            canSubscribe?: boolean;
            canPublishData?: boolean;
            roomRecord?: boolean;
            roomAdmin?: boolean;
        } = {
            canPublish: true,
            canSubscribe: true,
            canPublishData: true,
            roomRecord: false,
            roomAdmin: false
        }
    ): Promise<string> => {
        try {
            const response = await httpClient.post(
                `${this.apiUrl}/token`,
                {
                    roomName,
                    participantIdentity,
                    participantName,
                    permissions,
                },
                {
                    headers: {
                        'Authorization': 'Bearer ' + token
                    }
                }
            );
            return (response.data as any).token;
        } catch (error: any) {
            throw new Error(
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Failed to generate token'
            );
        }
    }

    /**
     * Fetch API token for LiveKit egress operations
     * @param token User authentication token
     * @returns Promise<string> API token for egress operations
     */
    fetchApiToken = async (token: string): Promise<string> => {
        try {
            const response = await httpClient.get(
                `${this.apiUrl}/egress/token`,
                {
                    headers: {
                        'Authorization': 'Bearer ' + token
                    }
                }
            );
            return (response.data as any).token;
        } catch (error: any) {
            throw new Error(
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Failed to fetch API token'
            );
        }
    };
}

export const simpleLiveStudioApi = new SimpleLiveStudioApiService();
